import React, { useState } from "react";
import {
  Typography,
  makeStyles,
  Divider,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  IconButton,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from "@material-ui/core";
import { DataGrid } from "@material-ui/data-grid";
import FormatListNumberedIcon from "@material-ui/icons/FormatListNumbered";
import moment from "moment";
import { useQuery, useMutation } from "@apollo/react-hooks";
import gql from "graphql-tag";
import EventForm from "./EventForm";

import { Card, CardContent } from "../../components/Card";
import Grid from "../../components/Grid";
import Button from "../../components/Button";
import { DateInput, TextInput } from "../../components/Form";
import Table from "../../components/Table";
import { useAlert } from "../../components/Alert";

const GET_EVENTS = gql`
  query events($latest: Boolean, $pageSize: Int, $page: Int) {
    events(latest: $latest, pageSize: $pageSize, page: $page) {
      count
      pageCount
      pastContents {
        id
        name
        dateStart
      }
      futureContents {
        id
        name
        dateStart
      }
    }
  }
`;

export default function ActivityAnnouncementTable() {
  const [open, setOpen] = useState(false);
  const [listOpen, setListOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [id, setId] = useState();
  const Alert = useAlert();

  const useStyles = makeStyles({
    DocumentContainerSpaceEvenly: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-evenly",
    },
    DocumentContainerCenter: {
      display: "flex",
      justifyContent: "center",
    },
  });
  const classes = useStyles();

  const { data, refetch, loading } = useQuery(GET_EVENTS, {
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    variables: {
      latest: false,
      pageSize: 10,
      page,
    },
    onError(error) {
      Alert.notice(`${error.message.replace("GraphQL error: ", "")}`);
    },
  });
  if (loading) {
    return (
      <Grid container justify="center">
        <CircularProgress color="secondary" />
      </Grid>
    );
  } else {
    return (
      <Card>
        <CardContent>
          <Grid
            container
            spacing={1}
            classes={{ root: classes.DocumentContainerCenter }}
          >
            <Typography style={{ fontSize: 30 }}> 活動時間公告</Typography>
          </Grid>
          <Grid container item justify={"flex-end"}>
            <Grid item xs={12} sm={2}>
              <Button
                size="small"
                color="primary"
                variant="contained"
                fullWidth
                onPress={() => setOpen(true)}
              >
                新增活動時間
              </Button>
              <EventForm
                open={open}
                data={{ id, type: "event" }}
                onClose={(v) => {
                  if (v === "refetch") refetch();
                  setOpen(false);
                }}
              />
            </Grid>
          </Grid>
        </CardContent>
        <Divider />
        <CardContent>
          <Grid
            container
            spacing={1}
            classes={{ root: classes.DocumentContainerSpaceEvenly }}
          >
            <Typography style={{ fontSize: 20 }}> 過去活動</Typography>
            <Divider orientation="vertical" flexItem style={{ height: 50 }} />
            <Typography style={{ fontSize: 20 }}> 未來活動</Typography>
          </Grid>
        </CardContent>
        <Divider />
        <CardContent>
          <Grid container spacing={1}>
            <Grid item xs={6} sm={6}>
              <Table
                data={data?.events.pastContents}
                header={["日期", "標題", "名單"]}
                columns={[
                  (item) => moment(item.dateStart).format("yyyy-MM-DD"),
                  "name",
                  (item) => (
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        setListOpen(true);
                      }}
                    >
                      <FormatListNumberedIcon />
                    </IconButton>
                  ),
                ]}
                onPress={(item) => {
                  setOpen(true);
                  setId(item.id);
                }}
              />
              <NameTable open={listOpen} onClose={() => setListOpen(false)} />
            </Grid>
            <Grid item xs={6} sm={6}>
              <Table
                data={data?.events.futureContents}
                header={["日期", "標題", "名單"]}
                columns={[
                  (item) => moment(item.dateStart).format("yyyy-MM-DD"),
                  "name",
                  (item) => (
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        setListOpen(true);
                      }}
                    >
                      <FormatListNumberedIcon />
                    </IconButton>
                  ),
                ]}
                onPress={(item) => {
                  setOpen(true);
                  setId(item.id);
                }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  }
}

function NameTable({ open, onClose = () => {} }) {
  const Alert = useAlert();
  const columns = [
    {
      field: "_number",
      headerName: "編號",
      width: 90,
      sortable: false,
      editable: false,
    },
    {
      field: "_name",
      headerName: "學員姓名",
      width: 115,
      sortable: false,
      editable: false,
    },
    {
      field: "_phone",
      headerName: "連絡電話",
      width: 115,
      sortable: false,
      editable: false,
    },
    {
      field: "_quantity",
      headerName: "參加人數",
      editable: true,
      width: 115,
      sortable: false,
    },
    {
      field: "_advance",
      headerName: "預收訂金",
      editable: true,
      width: 115,
      sortable: false,
    },
    {
      field: "_allMoney",
      headerName: "應收金額",
      editable: true,
      width: 115,
      sortable: false,
    },
    {
      field: "_business",
      headerName: "服務業務",
      width: 115,
      sortable: false,
      editable: false,
    },
    {
      field: "_remark",
      headerName: "備註事項",
      editable: true,
      width: 230,
      sortable: false,
    },
    {
      field: "_signIn",
      headerName: "簽到狀態",
      editable: true,
      width: 115,
      sortable: false,
      type: "singleSelect",
      valueOptions: [
        { label: "已簽到", value: "已簽到" },
        { label: "未簽到", value: "未簽到" },
      ],
    },
    {
      field: "_explanation",
      headerName: "說明事項",
      editable: true,
      width: 115,
      sortable: false,
    },
  ];
  const rows = [
    {
      id: "1",
      _number: "1",
      _name: "bo",
      _phone: "0921657852",
      _quantity: "1",
      _advance: "500",
      _allMoney: "1500",
      _business: "BOBO",
      _remark: "",
      _signIn: "",
      _explanation: "已取消",
    },
    {
      id: "2",
      _number: "2",
      _name: "boBo",
      _phone: "0926547852",
      _quantity: "2",
      _advance: "500",
      _allMoney: "1500",
      _business: "BOBO",
      _remark: "1231321313212",
      _signIn: "",
      _explanation: "已取消",
    },
  ];
  // const DetailInfo=(text)=> {
  //   if (text.length > 6) {
  //     return Alert.notify(text);
  //   }
  // }
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  return (
    <Dialog open={open} fullScreen={fullScreen} fullWidth maxWidth={"lg"}>
      <Card>
        <CardContent>
          <Grid container spacing={1} justify="center">
            <Typography style={{ fontSize: 25 }}>
              {"法拍入門說明會 報名名單"}
            </Typography>
          </Grid>
        </CardContent>
        <Divider />
        <CardContent>
          <div style={{ height: 300 }}>
            <DataGrid
              rows={rows}
              columns={columns}
              disableColumnMenu
              disableSelectionOnClick
              pageSize={5}
              rowsPerPageOptions={[5]}
              pagination
              // onCellClick={(e) => {
              //   DetailInfo(e.formattedValue);
              // }}
              showCellRightBorder={true}
            />
          </div>
        </CardContent>
        <Divider />
        <CardContent>
          <Grid container spacing={1} direction="row" justify="space-evenly">
            <Typography style={{ fontSize: 20 }}>
              {"應到人數:" + "位"}
            </Typography>
            <Typography style={{ fontSize: 20 }}>
              {"實到人數:" + "位"}
            </Typography>
            <Typography style={{ fontSize: 20 }}>
              {"未到人數:" + "位"}
            </Typography>
          </Grid>
        </CardContent>
        <Divider />
        <CardContent>
          <Grid container spacing={1} direction="row" justify="space-evenly">
            <Typography style={{ fontSize: 20 }}>
              {"應收總計:" + "元"}
            </Typography>
            <Typography style={{ fontSize: 20 }}>
              {"預收總計:" + "元"}
            </Typography>
            <Typography style={{ fontSize: 20 }}>
              {"實收總計:" + "元"}
            </Typography>
            <Typography style={{ fontSize: 20 }}>
              {"費用總計:" + "元"}
            </Typography>
          </Grid>
        </CardContent>
        <Divider />
        <CardContent>
          <Grid container spacing={1} justify="center">
            <Typography style={{ fontSize: 20 }}>
              {"BO" + ":位" + "[ 元]"}
            </Typography>
          </Grid>
        </CardContent>
        <Divider />
        <DialogActions>
          <Grid container spacing={1} justify="center">
            <Grid item>
              <Button variant="outlined" onPress={onClose} color="primary">
                返回
              </Button>
            </Grid>
            <Grid item>
              <Button variant="contained" onPress={() => {}} color="primary">
                儲存
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
      </Card>
    </Dialog>
  );
}
